import React from "react";
import { Grid, useMediaQuery } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "redux/rootReducer";
import { loadSummaryByAssetClass } from "redux/summary/summaryFunctions";
import { getReportDateSelector } from "redux/user/user";
import Table from "components/Table";
import { Cell } from "react-table";
import { asEur, asPercent } from "utility/numbers";
import { Summary } from "types/Summary";
import { AssetClass } from "types/AssetClass";

interface Props {
  assetClass: AssetClass;
}

const SummaryList = (props: Props) => {
  const { assetClass } = props;

  const { t } = useTranslation();

  const dispatch = useDispatch();

  const matchesMediaQuery = useMediaQuery("(min-width: 960px)");

  const reportControlId = useSelector(
    (state: RootState) => state.user.reportControlId
  );
  const reportDate = useSelector(getReportDateSelector);

  const summary = useSelector(
    (state: RootState) =>
      state.summary[AssetClass[assetClass] as keyof typeof AssetClass]?.data ??
      []
  );
  const loading = useSelector(
    (state: RootState) =>
      state.summary[AssetClass[assetClass] as keyof typeof AssetClass]?.loading
  );

  React.useEffect(() => {
    if (reportControlId && reportDate) {
      dispatch(
        loadSummaryByAssetClass(assetClass, reportControlId, reportDate)
      );
    }
  }, [dispatch, reportControlId, reportDate, assetClass]);

  const summaryBody = summary.slice(
    0,
    summary.findIndex((x) => x.AssetClass === "Yhteensä")
  );
  const summaryFooter = summary.slice(
    summary.findIndex((x) => x.AssetClass === "Yhteensä")
  )[0];

  const summaryColumns = [
    {
      Header: t("current_summary"),
      accessor: "Yhteenveto",
      width: 200,
      minWidth: 170,
      paddingRight: "10px",
      Footer: summaryFooter?.Yhteenveto ?? "",
    },
    {
      Header: t("weight"),
      accessor: "Paino",
      minWidth: 70,
      width: 70,
      paddingRight: "10px",
      Cell: (cell: Cell<Summary, number>) =>
        asPercent(cell.value, undefined, true),
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: asPercent(summaryFooter?.Paino, undefined, true),
    },
    {
      Header: t("market_value"),
      accessor: "MarkkinaArvo",
      paddingRight: "10px",
      Cell: (cell: Cell<Summary, number>) => asEur(cell.value, undefined, true),
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: asEur(summaryFooter?.MarkkinaArvo, undefined, true),
    },
    {
      Header: t("acquisition_value"),
      accessor: "HankintaArvo",
      paddingRight: "10px",
      Cell: (cell: Cell<Summary, number>) => asEur(cell.value, undefined, true),
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: asEur(summaryFooter?.HankintaArvo, undefined, true),
    },
    {
      Header: t("dividend_and_other_profits"),
      accessor: "Tuottokassavirta",
      paddingRight: "10px",
      Cell: (cell: Cell<Summary, number>) => asEur(cell.value, undefined, true),
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: asEur(summaryFooter?.Tuottokassavirta, undefined, true),
    },
    {
      Header: t("total_profit"),
      accessor: "TuottoEur",
      paddingRight: "10px",
      Cell: (cell: Cell<Summary, number>) => asEur(cell.value, undefined, true),
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: asEur(summaryFooter?.TuottoEur, undefined, true),
    },
    {
      Header: t("total_profit"),
      accessor: "Tuotto",
      paddingRight: "10px",
      Cell: (cell: Cell<Summary, number>) =>
        asPercent(cell.value, undefined, true),
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: asPercent(summaryFooter?.Tuotto, undefined, true),
    },
    {
      Header: t("profit_1m"),
      whiteSpace: "nowrap",
      paddingRight: "10px",
      Cell: (cell: Cell<Summary, number>) => " ",
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: " ",
      hideTitle: true,
    },
    {
      Header: t("profit_3m"),
      whiteSpace: "nowrap",
      paddingRight: "10px",
      Cell: (cell: Cell<Summary, number>) => " ",
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: " ",
      hideTitle: true,
    },
    {
      Header: t("profit_ytd"),
      Cell: (cell: Cell<Summary, number>) => " ",
      textAlign: "right",
      fontFamily: "Lato light",
      paddingRight: "10px",
      Footer: " ",
      hideTitle: true,
    },
    {
      Header: t("latest_price"),
      paddingRight: "10px",
      Cell: (cell: Cell<Summary, number>) => " ",
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: " ",
      hideTitle: true,
    },
    {
      Header: "Muutosprosentti",
      width: 200,
      minWidth: 150,
      Cell: (cell: Cell<Summary, number>) => " ",
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: " ",
      hideTitle: true,
    },
  ];

  return (
    <Grid
      item
      sm={12}
      style={{
        padding: matchesMediaQuery ? "0 0 16px 0" : "0 16px 16px 16px",
        overflowY: "hidden",
      }}
    >
      <Table
        data={summaryBody}
        columns={summaryColumns}
        loading={loading}
        hasFooter
      />
    </Grid>
  );
};

export default React.memo(SummaryList);
